/**
 * Habits 习惯打卡页：习惯列表 + 今日快捷打卡 + 月历（HabitCalendar）+ 新建/编辑/归档/删除。
 * 注意：Habit 内嵌 logs/streak；upsertHabitLog 用 { date, count }（CreateHabitLogBody）。
 */
import { useMemo, useState } from 'react';
import {
  useHabits, useCreateHabit, useUpdateHabit, useDeleteHabit, useUpsertHabitLog, useAreas,
} from '../hooks/useTasks';
import { HabitCalendar } from '../components/HabitCalendar';
import {
  Card, Button, Input, Select, Badge, Spinner, ConfirmDialog, EmptyState, errMsg,
} from '../components/ui';
import { Icon } from '../components/icons';
import type { Habit, CreateHabitBody, UpdateHabitBody } from '../lib/types';

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function HabitsPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Habit | null>(null);
  const [deleting, setDeleting] = useState<Habit | null>(null);

  const { data: habits, isLoading, error } = useHabits();
  const upsertLog = useUpsertHabitLog();
  const deleteHabit = useDeleteHabit();
  const list = useMemo(() => habits ?? [], [habits]);
  const selected = useMemo(
    () => list.find((h) => h.id === selectedId) ?? list[0] ?? null,
    [list, selectedId],
  );

  async function toggleToday(habit: Habit) {
    const date = todayStr();
    const target = habit.targetCount || 1;
    const existing = (habit.logs ?? []).find((l) => l.date === date);
    const next = (existing?.count ?? 0) >= target ? 0 : target;
    try {
      await upsertLog.mutateAsync({ habitId: habit.id, body: { date, count: next } });
    } catch {
      /* 错误由全局 mutation 状态呈现 */
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">习惯</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            每日打卡，保持连续记录
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }}>
          <Icon name="plus" className="mr-1 h-4 w-4" />新建习惯
        </Button>
      </div>

      {isLoading && <div className="flex justify-center py-12"><Spinner className="h-8 w-8" /></div>}
      {error && <Card><p className="text-sm text-red-600 dark:text-red-400">{errMsg(error)}</p></Card>}

      {!isLoading && !error && list.length === 0 && (
        <EmptyState
          icon={<Icon name="habits" className="mx-auto h-10 w-10" />}
          title="暂无习惯"
          description="创建第一个习惯开始打卡"
        />
      )}

      {list.length > 0 && (
        <div className="grid gap-4 lg:grid-cols-5">
          <div className="space-y-3 lg:col-span-2">
            {list.map((h) => {
              const doneToday = (h.logs ?? []).some(
                (l) => l.date === todayStr() && l.count >= (h.targetCount || 1),
              );
              const active = selected?.id === h.id;
              return (
                <Card
                  key={h.id}
                  className={`cursor-pointer transition-colors ${active ? 'ring-2 ring-indigo-500' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleToday(h)}
                      disabled={upsertLog.isPending}
                      aria-label={doneToday ? '取消今日打卡' : '完成今日打卡'}
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                        doneToday
                          ? 'border-green-500 bg-green-500 text-white'
                          : 'border-slate-300 text-transparent hover:border-indigo-400 dark:border-slate-600'
                      }`}
                    >
                      <Icon name="check" className="h-5 w-5" />
                    </button>
                    <div
                      className="min-w-0 flex-1"
                      onClick={() => setSelectedId(h.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') setSelectedId(h.id); }}
                    >
                      <div className="flex items-center gap-2">
                        {h.color && (
                          <span
                            className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: h.color }}
                          />
                        )}
                        <p className="truncate font-medium text-slate-900 dark:text-white">{h.name}</p>
                      </div>
                      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                        {h.frequency} · 每日 {h.targetCount} 次
                        {h.streak > 0 && <span className="ml-2 text-orange-500">连续 {h.streak} 天</span>}
                      </p>
                    </div>
                    <Badge
                      className={doneToday
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300'
                        : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'}
                    >
                      {doneToday ? '今日已打卡' : '未打卡'}
                    </Badge>
                    <div className="flex flex-col gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setEditing(h); setFormOpen(true); }}
                        aria-label="编辑习惯"
                      >
                        <Icon name="edit" className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 dark:text-red-400"
                        onClick={() => setDeleting(h)}
                        aria-label="删除习惯"
                      >
                        <Icon name="trash" className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          <div className="lg:col-span-3">
            {selected && <HabitCalendar habit={selected} />}
          </div>
        </div>
      )}

      {formOpen && <HabitForm habit={editing} onClose={() => setFormOpen(false)} />}

      <ConfirmDialog
        open={!!deleting}
        title="删除习惯"
        message={`确定删除习惯「${deleting?.name}」吗？历史打卡记录将一并删除。`}
        confirmText="删除"
        danger
        busy={deleteHabit.isPending}
        onConfirm={() => {
          if (!deleting) return;
          void deleteHabit.mutateAsync(deleting.id).then(() => setDeleting(null)).catch(() => setDeleting(null));
        }}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}

function HabitForm({ habit, onClose }: { habit: Habit | null; onClose: () => void }) {
  const { data: areas } = useAreas();
  const createHabit = useCreateHabit();
  const updateHabit = useUpdateHabit();
  const [form, setForm] = useState(() => ({
    name: habit?.name ?? '',
    frequency: habit?.frequency ?? 'daily',
    targetCount: habit?.targetCount ?? 1,
    color: habit?.color ?? '',
    areaId: habit?.areaId ?? '',
  }));
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));
  const pending = createHabit.isPending || updateHabit.isPending;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) { setError('请填写习惯名称'); return; }
    if (form.targetCount < 1) { setError('每日次数至少为 1'); return; }
    try {
      if (habit) {
        // UpdateHabitBody 整体提交，可清空字段显式置 null
        const body: UpdateHabitBody = {
          name: form.name.trim(),
          frequency: form.frequency,
          targetCount: form.targetCount,
          color: form.color || null,
          areaId: form.areaId || null,
        };
        await updateHabit.mutateAsync({ id: habit.id, body });
      } else {
        // CreateHabitBody：name/frequency/targetCount 必填，其余仅已填时提交
        const body: CreateHabitBody = {
          name: form.name.trim(),
          frequency: form.frequency,
          targetCount: form.targetCount,
        };
        if (form.color) body.color = form.color;
        if (form.areaId) body.areaId = form.areaId;
        await createHabit.mutateAsync(body);
      }
      onClose();
    } catch (err) {
      setError(errMsg(err));
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <form
        onSubmit={submit}
        className="relative z-10 w-full max-w-md rounded-t-2xl bg-white p-6 shadow-xl dark:bg-slate-900 sm:rounded-2xl"
      >
        <h2 className="mb-4 text-lg font-semibold">{habit ? '编辑习惯' : '新建习惯'}</h2>
        {error && (
          <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/50 dark:text-red-400">
            {error}
          </div>
        )}
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">习惯名称 *</label>
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} required placeholder="如：晨跑、阅读" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium">频率 *</label>
              <Select value={form.frequency} onChange={(e) => set('frequency', e.target.value)}>
                <option value="daily">每天</option>
                <option value="weekly">每周</option>
                <option value="monthly">每月</option>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">每日目标次数 *</label>
              <Input
                type="number"
                min={1}
                value={form.targetCount}
                onChange={(e) => set('targetCount', Math.max(1, Number(e.target.value) || 1))}
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">颜色标记</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={form.color || '#10b981'}
                onChange={(e) => set('color', e.target.value)}
                className="h-9 w-14 cursor-pointer rounded border border-slate-300 bg-transparent dark:border-slate-600"
              />
              {form.color && (
                <Button variant="ghost" size="sm" type="button" onClick={() => set('color', '')}>
                  清除
                </Button>
              )}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">领域</label>
            <Select value={form.areaId} onChange={(e) => set('areaId', e.target.value)}>
              <option value="">未分类</option>
              {(areas ?? []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </Select>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>取消</Button>
          <Button type="submit" disabled={pending}>{pending ? '保存中…' : '保存'}</Button>
        </div>
      </form>
    </div>
  );
}
